<template>
    <div class="konva-campaign-sidebar" :class="{'is-sequence': isSequence, 'is-collapsed': isCollapsed}">
        <DsCollapsible
            :is-open="!isCollapsed"
            @toggle="toggleCollapse"
        >
            <template #header>
                <div
                    class="collapsible-header"
                    :class="{'is-sequence': isSequence, 'is-collapsed': isCollapsed}"
                    data-qa="toggle-collapse"
                >
                    <DsIcon :name="isCollapsed ? 'expand' : 'collapse'" />
                </div>
            </template>
            <template #default>
                <DsTabset class="cell-list-tabset">
                    <DsTab
                        v-if="!isSequence"
                        data-qa="when-tab"
                        :label="$t('funnel.sidebar.when.header.label')"
                        :action="() => selectedTab = $options.CELL_TYPE.WHEN"
                        :selected="selectedTab === $options.CELL_TYPE.WHEN"
                    >
                        <section v-if="selectedTab === $options.CELL_TYPE.WHEN" class="cell-list">
                            <DsInputField
                                v-model="searchText"
                                data-qa="search-input"
                                class="cell-search"
                                :placeholder="$t('funnel.sidebar.search.label.when')"
                            >
                                <template #leading>
                                    <DsIcon class="cell-search-icon" name="search" />
                                </template>
                            </DsInputField>
                            <div class="cell-list-container">
                                <div
                                    v-for="(cell, index) in filteredCells"
                                    :key="`${cell.name}_${index}`"
                                    :data-qa="`when-cell ${cell.name}`"
                                    class="cell-row when-cell"
                                >
                                    <div
                                        draggable
                                        class="draggable-cell"
                                        @dragstart="(event) => onDragStart(event, cell, $options.CELL_TYPE.WHEN)"
                                    >
                                        <DsIcon v-if="cell.icon" class="cell-icon" :name="cell.icon" />

                                        <img
                                            :class="{ 'external-cell-icon': !!cell.isIntegration }"
                                            class="svg-cell-icon"
                                            :src="iconPath(cell, 'green')"
                                        />

                                        <span class="cell-label-display">{{ cell.displayLabel }}</span>

                                        <div class="cell-icon-overlay-sublabel-container">
                                            <div v-if="cell.subLabel" class="cell-icon-overlay-sublabel">
                                                {{ cell.subLabel }}
                                            </div>
                                            <div class="hover-icon">
                                                <DsIcon class="drag-icon-sidebar" name="move" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="!filteredCells.length">
                                    {{ noResultsText }}
                                </div>
                            </div>
                        </section>
                    </DsTab>
                    <DsTab
                        data-qa="then-tab"
                        :label="$t('funnel.sidebar.then.header.label')"
                        :action="() => selectedTab = $options.CELL_TYPE.THEN"
                        :selected="selectedTab === $options.CELL_TYPE.THEN"
                    >
                        <section
                            v-if="selectedTab === $options.CELL_TYPE.THEN"
                            class="cell-list"
                            :class="{'is-sequence': isSequence}"
                        >
                            <DsInputField v-model="searchText" class="cell-search" :placeholder="$t('funnel.sidebar.search.label.then')">
                                <template #leading>
                                    <DsIcon class="cell-search-icon" name="search" />
                                </template>
                            </DsInputField>

                            <div class="cell-list-container">
                                <div
                                    v-for="(cell, index) in filteredCells"
                                    :key="`${cell.name}_${index}`"
                                    :data-qa="`then-cell ${cell.name}`"
                                    class="cell-row then-cell"
                                >
                                    <div
                                        draggable
                                        class="draggable-cell"
                                        @dragstart="(event) => onDragStart(event, cell, $options.CELL_TYPE.THEN)"
                                    >
                                        <DsIcon v-if="cell.icon" class="cell-icon" :name="cell.icon" />
                                        <img
                                            :class="{ 'external-cell-icon': !!cell.isIntegration }"
                                            class="svg-cell-icon"
                                            :src="iconPath(cell, 'blue')"
                                        />
                                        <span
                                            :data-qa="cell.name"
                                            class="cell-label-display"
                                        >
                                            {{ cell.displayLabel }}
                                        </span>
                                        <div class="cell-icon-overlay-sublabel-container">
                                            <div v-if="cell.subLabel" class="cell-icon-overlay-sublabel">
                                                {{ cell.subLabel }}
                                            </div>
                                            <div class="hover-icon">
                                                <DsIcon class="drag-icon-sidebar" name="move" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="!filteredCells.length">
                                    {{ noResultsText }}
                                </div>
                            </div>
                        </section>
                    </DsTab>
                </DsTabset>
            </template>
        </DsCollapsible>
    </div>
</template>

<script>
import {
    CELL_TYPE,
    PROCESS_EMAIL_CONFIRM,
    WHEN_CELL_OPTIONS,
    THEN_CELL_OPTIONS,
} from '../utils/editor.constants';
import { getIconPathByCellType } from '../utils/cell/automation-display-utils';
import {
    displayLabel,
} from '../utils/add-elements.utils';

export default {
    CELL_TYPE,
    props: {
        enabledCellTypes: {
            type: Array,
            required: true,
        },
        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
        isSequence: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['toggle-collapse'],
    data() {
        return {
            searchText: '',
            selectedTab: this.isSequence ? CELL_TYPE.THEN : CELL_TYPE.WHEN,
            isCollapsed: false,
        };
    },

    computed: {
        filteredCells() {
            const categorizedCells = this.selectedTab === CELL_TYPE.WHEN
                ? WHEN_CELL_OPTIONS
                : THEN_CELL_OPTIONS;

            const enabledCells = categorizedCells.filter((cell) => (this.enabledCellTypes.includes(cell.name)))
                .map((cell) => ({
                    ...cell,
                    displayLabel: this.$t(displayLabel(this.selectedTab, cell.name, this.isSequence)),
                    isIntegration: false,
                }));

            const integrationCells = this.enabledIntegrations.filter((integration) => {
                return integration.sidebarType.toUpperCase() === this.selectedTab.toUpperCase();
            }).map((integration) => {
                return {
                    ...integration,
                    displayLabel: integration.label,
                    isIntegration: true,
                };
            });

            const searchWords = this.searchText.toLowerCase().trim().split(' ');

            return [...enabledCells, ...integrationCells].filter((cell) => {
                if (this.isSequence && cell.name === PROCESS_EMAIL_CONFIRM) return false;
                if (!this.searchText) return true;

                const searchString = `${cell.name} ${cell.type} ${cell.displayLabel}`;
                const cellNameWords = searchString.toLowerCase().trim().split(' ');

                return searchWords.every((searchWord) => cellNameWords.some((cellNameWord) => cellNameWord.includes(searchWord)));
            });
        },

        noResultsText() {
            return `${this.$t('funnel.sidebar.search.no.results')} "${this.searchText}"`;
        },
    },

    methods: {
        iconPath(cell, color) {
            if (cell.iconUrl) return cell.iconUrl;

            return getIconPathByCellType(cell.name, color);
        },

        onDragStart(event, cell, cellType) {
            event.dataTransfer.dropEffect = 'create-cell';
            event.dataTransfer.effectAllowed = 'create-cell';

            const img = event.currentTarget.querySelector('.svg-cell-icon');

            event.dataTransfer.setDragImage(img, 24, 24);
            event.dataTransfer.setData('cellType', this.selectedTab);

            if (cell.isIntegration) {
                event.dataTransfer.setData('cellIntegrationType', cell.name);
                event.dataTransfer.setData('cellName', cell.metaType);
                event.dataTransfer.setData('cellIntegrationId', cell.id);
                cell.defaultName = cell.name;
            } else {
                event.dataTransfer.setData('cellName', cell.name);
                if (cell.defaultName) cell.defaultName = this.$t(cell.defaultName);
            }

            const defaultName = cell.defaultName || this.$t(`funnel.sidebar.${cellType}.cell.${cell.label || cell.name}.label`);

            event.dataTransfer.setData('cellDefaultName', defaultName);
        },
        toggleCollapse() {
            this.isCollapsed = !this.isCollapsed;
            this.$emit('toggle-collapse', this.isCollapsed);
            this.$nextTick(() => {
                const collapsibleContent = this.$el.querySelector('.collapsible-content');

                if (collapsibleContent && !this.isCollapsed) {
                    collapsibleContent.style.height = 'auto';
                }
            });
        },
    },
};

</script>

<style scoped lang="scss">
$sidebar-header-height: px-to-rem(100px);
$sidebar-header-height-in-sequence: px-to-rem(200px);

.konva-campaign-sidebar {
    width: px-to-rem(400px);
    height: calc(100vh - 72px);
    overflow: hidden;
    animation-name: slideInLeft;
    transition: all 0.5s ease-out;
    animation-duration: 0.5s;
    animation-fill-mode: both;
    &.is-sequence {
        height: calc(100vh - 200px);
    }

    &.is-collapsed {
        width: 0;
    }

    .collapsible-header {
        --icon-color: #{$color-ink-800};
        --icon-size: #{$icon-size-small};

        position: absolute;
        top: px-to-rem(78px);
        left: px-to-rem(356px);
        transition: left 0.6s ease;

        &.is-sequence {
            top: px-to-rem(76px);
            left: px-to-rem(366px);
        }

        &.is-collapsed {
            left: $spacing-200;
            box-shadow: $elevation-z1;
            background-color: $color-paper;
        }
        border-radius: 20%;
        padding: $spacing-100;
        cursor: pointer;
        z-index: 10;
    }

    ::v-deep .collapsible-content {
        height: 100%;
    }
}

.cell-list {
    display: grid;
    grid-template-rows: auto 1fr 1rem;
    height: calc(100vh - $sidebar-header-height);
    &.is-sequence {
        height: calc(100vh - $sidebar-header-height-in-sequence);
    }
}
.cell-list-container {
    overflow-y: auto;
    overflow-x: hidden;
}
.cell-search {
    padding: $spacing-200 $spacing-200 $spacing-200 0;
    margin-bottom: 0;
}

.draggable-cell {
    cursor: pointer;
    display: grid;
    grid-template-columns: 3.5rem 1fr auto;
    grid-template-rows: auto;
    text-wrap: nowrap;
    margin-right: 1rem;

    &:hover {
        background-color: $color-gray-050;

        .hover-icon {
            display: block;
        }
    }
}

.cell-icon-overlay-sublabel {
    margin: auto $spacing-200;
    padding: $spacing-050 $spacing-050;
    border-radius: $spacing-050;
    color: $color-paper;
    font-weight: bold;
    font-size: $font-size-sm;
    line-height: px-to-rem(12px);
    box-shadow: $elevation-z1;
}

.when-cell .cell-icon-overlay-sublabel {
    background-color: $color-green-400;
}

.then-cell .cell-icon-overlay-sublabel {
    background-color: $color-blue-400;
}

.cell-icon-overlay-sublabel-container {
    display: flex;
    margin: auto 0;
    height: 100%;
    border-bottom: 1px solid $color-gray-400;
}

.cell-label-display {
    display: flex;
    align-items: center;
    height: 100%;
    border-bottom: 1px solid $color-gray-400;
    font-size: $font-size-md;
    margin: auto 0;
    padding-top: $spacing-200;
    padding-bottom: $spacing-200;
    color: var(--list-item-title-color, #{$color-ink});
    line-height: 1.5;
}

.cell-subtext {
    font-size: $font-size-sm;
    margin-top: $spacing-100;
}

.cell-row .icon {
    --icon-size: #{1.5rem};
    margin: auto;
}

.svg-cell-icon, .external-cell-icon {
    margin: auto;
    width: 100%;
    height: 100%;
    padding: $spacing-200;
}

.cell-row.when-cell, .cell-row.integration-cell {
    .icon {
        --icon-color: #{$color-green-400};
        margin: auto;
        width: 1.5rem;
        height: 1.5rem;
    }
}

.cell-row.then-cell {
    .icon {
        --icon-color: #{$color-blue-400};
    }
}
.hover-icon {
    margin: $spacing-125;
    align-content: center;
    display: none;
}

.drag-icon-sidebar {
    fill: #{$color-beige-700};
}

.konva-campaign-sidebar ::v-deep {
    .cell-list-tabset.tabset > .tab-content {
        position: inherit;
        left: 0;
        top: 0;
        clear: both;
        visibility: visible;
    }
    .cell-list-tabset.tabset > div.tab-header > div > ul {
        margin: 0 auto 0 0;
    }
}
</style>

<style lang="scss">
.tablist.tabs li.tab-header-item:nth-child(2).tab-header-item-active {
    border-color: $color-blue-600;
}

.konva-campaign-sidebar.is-sequence .tablist.tabs {
    display: none;
}
</style>
